import type {
  CreateCustodianPayload,
  CustodianContactFields,
  CustodianOrigin,
  CustodianTypeOption,
} from '../api';
import { ROLE_OPTIONS } from '../../../shared/utils/roleLabels';

/**
 * Regras puras do cadastro de custodiário (#541).
 *
 * O campo "Tipo" virou um catálogo com origem: perfis de usuário continuam
 * vindo de `roles`, e Departamento/Cliente vêm dos cadastros de área e de
 * cliente. A origem decide o rótulo do segundo campo, a mensagem de ajuda e
 * qual chave o POST envia. Fica fora do componente para ser testável sem DOM.
 */

export const ROTULO_DA_ORIGEM: Record<CustodianOrigin, string> = {
  user: 'Usuário do Nexos',
  department: 'Departamento',
  client: 'Cliente',
};

/** Rótulo do combo que depende do Tipo selecionado. */
export function rotuloDoAlvo(origin: CustodianOrigin): string {
  return `${ROTULO_DA_ORIGEM[origin]} *`;
}

/** Origem de um slug do catálogo; `null` quando o tipo não está disponível. */
export function origemDoTipo(
  options: CustodianTypeOption[],
  slug: string,
): CustodianOrigin | null {
  return options.find((option) => option.slug === slug)?.origin ?? null;
}

/** Texto de apoio abaixo do combo de origem. */
export function textoAjudaDoAlvo(
  origin: CustodianOrigin,
  state: { carregando: boolean; quantidade: number },
): string {
  if (state.carregando) {
    return origin === 'user' ? 'Carregando usuários...' : 'Carregando opções...';
  }
  if (state.quantidade === 0) {
    return origin === 'user'
      ? 'Nenhum usuário ativo com este perfil. Cadastre em Cadastros → Usuários.'
      : origin === 'department'
        ? 'Nenhum departamento ativo. Cadastre em Cadastros → Áreas.'
        : 'Nenhum cliente ativo. Cadastre em Cadastros → Clientes.';
  }
  return origin === 'user'
    ? 'E-mail e telefone são preenchidos automaticamente.'
    : `Selecione o ${ROTULO_DA_ORIGEM[origin].toLowerCase()} que assume a responsabilidade.`;
}

/** Mensagem de validação quando a origem não foi escolhida. */
export function mensagemSelecaoObrigatoria(origin: CustodianOrigin): string {
  return `Selecione o ${ROTULO_DA_ORIGEM[origin]}`;
}

export interface EntradaDoFormulario {
  origin: CustodianOrigin;
  /** `userId`, `departmentId` ou `clientId`, conforme a origem. */
  selecionadoId: string;
  email?: string;
  phone?: string;
  jobTitle?: string;
}

/**
 * Monta o payload do POST com **uma** origem e os contatos não vazios.
 * Campos em branco não são enviados para o backend aplicar o fallback.
 */
export function montarCustodianPayload(entrada: EntradaDoFormulario): CreateCustodianPayload {
  const contato: CustodianContactFields = {};
  if (entrada.email?.trim()) contato.email = entrada.email.trim();
  if (entrada.phone?.trim()) contato.phone = entrada.phone.trim();
  if (entrada.jobTitle?.trim()) contato.jobTitle = entrada.jobTitle.trim();

  if (entrada.origin === 'department') {
    return { departmentId: entrada.selecionadoId, ...contato };
  }
  if (entrada.origin === 'client') {
    return { clientId: entrada.selecionadoId, ...contato };
  }
  return { userId: entrada.selecionadoId, ...contato };
}

/**
 * Catálogo de fallback para o filtro da listagem enquanto a API responde —
 * mantém Departamento e Cliente visíveis mesmo offline.
 */
export const TIPOS_FALLBACK: CustodianTypeOption[] = [
  ...ROLE_OPTIONS.map((role) => ({
    slug: role.value,
    name: role.label,
    origin: 'user' as const,
  })),
  { slug: 'department', name: 'Departamento', origin: 'department' as const },
  { slug: 'client', name: 'Cliente', origin: 'client' as const },
].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

/** Opções do select de filtro por Tipo. */
export function opcoesDeTipoParaFiltro(
  options: CustodianTypeOption[] | undefined,
): Array<{ value: string; label: string }> {
  return (options && options.length > 0 ? options : TIPOS_FALLBACK).map((option) => ({
    value: option.slug,
    label: option.name,
  }));
}
